import {ChangeDetectionStrategy, Component, ElementRef, OnChanges, Renderer2, signal, SimpleChanges, viewChild} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {finnNærmesteForelderElementSomMatcher} from "felles-ng2/støttefunksjoner/dom";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {TiptapNodeViewContentDirective} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/ngx-tiptap/src/lib/node-view-content.directive";
import {AngularNodeViewComponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/ngx-tiptap/src/lib/node-view.component";
import {RiktekstRedigeringTabellRammevalgKomponent, SkjemaType} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/komponenter/tabell-rammevalg/tabell-rammevalg";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {Celleramme, erLikeRammevalg} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/utvidelser/TabellcelleRamme";
import {NgxColorsModule} from "ngx-colors";
import {TranslateModule} from "@ngx-translate/core";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {MatDivider} from "@angular/material/divider";
import {NgTemplateOutlet} from "@angular/common";
import {erIkkeTomCellSelection} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/tiptapHjelpere";
import {CellSelection} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/prosemirror-eksporter";
import {valgteTabellCeller} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/utvidelser/TabellCelle";
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";

const debug = initDebugger('TabellCelleKomponent');

/** Komponent som rendrer td-celle i tabell med gui for å redigere celler.
 * Lever i kompaniskap med {@link TabellCelle}.
 * Merk at {@link TabellCelle} er ansvarlig for å generer html-koden som leveres til feltkontroll knyttet til {@link RiktekstRedigeringKomponent}.
 * MERK at denne ikke kan benyttes utenfor tiptap editor */
@Component({
    selector: 'td',
    templateUrl: 'tabellcelle.html',
    styleUrl: 'tabellcelle.scss',
    host: {
        '(mousedown)': 'stoppMusehendelserSomIkkeSkalPropagereTilTiptap($event)',
        '(click)': 'stoppMusehendelserSomIkkeSkalPropagereTilTiptap($event)'
    },
    imports: [
        MatIcon,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
        TiptapNodeViewContentDirective,
        RiktekstRedigeringTabellRammevalgKomponent,
        MatIconButton,
        NgxColorsModule,
        TranslateModule,
        ReactiveFormsModule,
        MatDivider,
        NgTemplateOutlet
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RiktekstRedigeringTabellCelleKomponent extends AngularNodeViewComponent implements OnChanges {

    rammevalgSkjema = RiktekstRedigeringTabellRammevalgKomponent.opprettSkjema();
    bakgrunnsfargeKontroll = new FormControl<string | undefined>(undefined);

    erCelleIFoersteKolonne = signal(false);
    erCelleIFoersteRad = signal(false);
    margIPiksler = signal<number|null>(null);

    /** Utvalg som var aktivt når tabell celle/rad ble markert  */
    private opprinneligUtvalg: any = null;
    private celleinnholdKontainerElement = viewChild.required(TiptapNodeViewContentDirective, {read: ElementRef});

    constructor(private el: ElementRef<HTMLElement>, private renderer: Renderer2) {
        super();
        this.oppdaterNodeVedBakgrunnsfargeEndringer();
        this.oppdaterNodeVedRammevalgEndringer();
        this.kopierPaddingTilKontainerelement();
    }

    fjernMarger() {
        this.editor().chain().focus().fjernCelleMarg().run()
    }

    settMarger(marg: number) {
        this.editor().chain().focus().setCelleMarg(marg).run()
    }

    /** Skjema for rammevalg aktivert via celle-menyen */
    rammevalgForCelle(): SkjemaType {
        return this.rammevalgEnkel();
    }

    /** Skjema for rammevalg aktivert via kolonne-menyen */
    rammevalgForKolonne(): SkjemaType {
        return this.rammevalgEnkel();
    }

    /** Skjema for rammevalg aktivert via rad-menyen */
    rammevalgForRad(): SkjemaType {
        return this.rammevalgEnkel();
    }

    /** Skjema for rammevalg aktivert via tabell-menyen */
    rammevalgForTabell(): SkjemaType {
        return this.rammevalgEnkel();
    }

    beregnErFørsteRadEllerKolonne() {
        const pos = this.getPos()();
        let resolve = this.view().state.doc.resolve(pos);
        this.erCelleIFoersteKolonne.set(erNil(resolve.nodeBefore));

        const rad = resolve.parent!;
        const tabell = resolve.node(-1);
        const førsteRad = tabell.firstChild!;
        this.erCelleIFoersteRad.set(rad === førsteRad);
    }

    erCellebreddeSatt(): boolean {
        return erIkkeNil(this.node().attrs.colwidth);
    }

    statiskCellebredde(): number {
        if (!this.erCellebreddeSatt()) return 0;
        return this.node().attrs.colwidth[0];
    }

    nullstillKolonnebredde() {
        this.editor().chain().focus().resettBredder().run();
    }

    fjernBakgrunnsfarge() {
        this.editor().chain().focus().fjernCelleBakgrunnsfarge().run();
    }

    slettKolonne() {
        this.editor().chain().focus().deleteColumn().run();
    }

    leggTilKolonneFoer() {
        this.editor().chain().focus().addColumnBefore().run();
    }

    leggTilKolonneEtter() {
        this.editor().chain().focus().addColumnAfter().run();
    }

    leggTilRadFoer() {
        this.editor().chain().focus().addRowBefore().run();
    }

    leggTilRadEtter() {
        this.editor().chain().focus().addRowAfter().run();
    }

    slettRad() {
        this.editor().chain().focus().deleteRow().run();
    }

    slettTabell() {
        this.editor().chain().focus().deleteTable().run();
    }

    kanSplitteCelle(): boolean {
        return this.editor().can().splitCell();
    }

    splittCelle() {
        this.editor().chain().focus().splitCell().run();
    }

    kanSlaaSammenCeller(): boolean {
        return this.editor().can().mergeCells();
    }

    slaaSammenCeller() {
        this.editor().chain().focus().mergeCells().run();
    }

    /** Kalles når cellemeny åpnes */
    velgCelle(rammevalgKomp: RiktekstRedigeringTabellRammevalgKomponent) {
        this.opprinneligUtvalg = this.editor().state.selection;
        if (!erIkkeTomCellSelection(this.editor().state.selection))
            this.editor().commands.velgCelle(this.getPos()());
        this.bakgrunnsfargeKontroll.setValue(this.node().attrs.bakgrunnsfarge);
        this.oppdaterStilvalgFraValgteCeller(rammevalgKomp);
    }

    /** Kalles når cellemenyen lukkes */
    cellemenyLukket() {
        this.resetTilOpprinneligUtvalg();
    }

    /** Kalles når radmenyen åpnes */
    velgRad(rammevalgKomp: RiktekstRedigeringTabellRammevalgKomponent) {
        this.opprinneligUtvalg = this.editor().state.selection;
        this.editor().commands.velgRad(this.getPos()());
        this.oppdaterStilvalgFraValgteCeller(rammevalgKomp);
    }

    /** Kalles når radmenyen lukkes */
    radmenyLukket() {
        this.resetTilOpprinneligUtvalg();
    }

    /** Kalles når kolonnemenyen åpnes */
    velgKolonne(rammevalgKomp: RiktekstRedigeringTabellRammevalgKomponent) {
        this.opprinneligUtvalg = this.editor().state.selection;
        this.editor().commands.velgKolonne(this.getPos()());
        this.oppdaterStilvalgFraValgteCeller(rammevalgKomp);
    }

    /** Kalles når kolonnemenyen lukkes */
    kolonnemenyLukket() {
        this.resetTilOpprinneligUtvalg();
    }

    /** Kalles når tabellmenyen åpnes */
    velgTabell(rammevalgKomp: RiktekstRedigeringTabellRammevalgKomponent) {
        this.opprinneligUtvalg = this.editor().state.selection;
        this.editor().commands.velgAlleCeller(this.getPos()());
        this.oppdaterStilvalgFraValgteCeller(rammevalgKomp);
    }

    /** Kalles når tabellmenyen lukkes */
    tabellmenyLukket() {
        this.resetTilOpprinneligUtvalg();
    }

    /** Klikk på knapper skal ikke propagere til tiptap.
     * Merk at ikke ALLE hendelser kan stoppes, da vil ikke  tableColumnResizing pluginet som aktiveres via https://www.tiptap.dev/api/nodes/table fungere */
    stoppMusehendelserSomIkkeSkalPropagereTilTiptap(event: UIEvent) {
        let medIkkePropagerHendelserTilTiptapAttr = (element: HTMLElement) => element.hasAttribute('data-ikkePropagerHendelserTilTiptap');
        if (erIkkeNil(finnNærmesteForelderElementSomMatcher(event.target as HTMLElement, medIkkePropagerHendelserTilTiptapAttr)))
            event.stopPropagation();
    }

    // ng lifsyklus-metoder

    ngOnChanges(changes: SimpleChanges): void {
        //vi må reagere på at nye rader/kolonner har blitt lagt til
        //ideelt sett burde vi kun reagere på endringer i tabell-noden,
        //feks ved å lytt til transaction-hendelse (this.editor().on('transaction', ()=>{}))og inspisere denne for å se om
        //tabell-node er endret.. men foreløpig antar vi at beregnErFørsteRadEllerKolonne()
        //koster lite og ikke utgjør nevneverdig overhead
        this.beregnErFørsteRadEllerKolonne();
    }

    // private hjelpemetoder

    private resetTilOpprinneligUtvalg() {
        if (erNil(this.opprinneligUtvalg)) return;

        const {from, to} = this.opprinneligUtvalg;
        this.editor().chain().focus().setTextSelection({from, to}).run();
    }

    private oppdaterNodeVedBakgrunnsfargeEndringer() {
        this.bakgrunnsfargeKontroll.valueChanges
          .pipe(takeUntilDestroyed())
          .subscribe(farge =>
            this.editor().chain().focus().setCelleBakgrunnsfarge(farge!).run());
    }

    private oppdaterNodeVedRammevalgEndringer() {
        this.rammevalgSkjema.valueChanges
          .pipe(takeUntilDestroyed())
          .subscribe(rammevalg =>
            RiktekstRedigeringTabellRammevalgKomponent.erIngenRammerSatt(rammevalg) ?
              this.editor().chain().focus().fjernCelleRamme().run() :
              this.editor().chain().focus().setCelleRamme(RiktekstRedigeringTabellRammevalgKomponent.somCelleramme(rammevalg)).run());
    }

    /** Sørg for at padding satt på td-node (via TabellcelleMarger.ts tiptap utvidelsen) kopieres til .celleinnhold-kontainer elementet.
     * Padding på td-elementet er overstyrt og satt til 0 da padding på td går i beina på annen stiling, se tabellcelle.scss for detaljer */
    private kopierPaddingTilKontainerelement(){
        toObservable(this.node).pipe(takeUntilDestroyed())
          .subscribe(tdNode => {
              console.log('#kopierPaddingTilKontainerelement this.el.nativeElement.style.paddingLeft:', this.el.nativeElement.style.paddingLeft);
              console.log('#kopierPaddingTilKontainerelement tdNode.attrs.marg:', tdNode.attrs.marg);
              this.renderer.setStyle(this.celleinnholdKontainerElement().nativeElement, 'padding-left', this.el.nativeElement.style.paddingLeft);
              this.renderer.setStyle(this.celleinnholdKontainerElement().nativeElement, 'padding-top', this.el.nativeElement.style.paddingTop);
              this.renderer.setStyle(this.celleinnholdKontainerElement().nativeElement, 'padding-right', this.el.nativeElement.style.paddingRight);
              this.renderer.setStyle(this.celleinnholdKontainerElement().nativeElement, 'padding-bottom', this.el.nativeElement.style.paddingBottom);

              this.renderer.setStyle(this.celleinnholdKontainerElement().nativeElement, 'padding-left', tdNode.attrs.marg+'px');
              this.renderer.setStyle(this.celleinnholdKontainerElement().nativeElement, 'padding-top', tdNode.attrs.marg+'px');
              this.renderer.setStyle(this.celleinnholdKontainerElement().nativeElement, 'padding-right', tdNode.attrs.marg+'px');
              this.renderer.setStyle(this.celleinnholdKontainerElement().nativeElement, 'padding-bottom', tdNode.attrs.marg+'px');
          });
    }

    /** Foreløpig eksponerer vi kun valg for venstre/topp/høyre/bunn.
     * Øvrige mulige valg krever logikk som avhenger av hvilke/hvor mange celler som er valgt,
     * foreløpig hopper vi over denne kompleksiteten*/
    private rammevalgEnkel() {
        this.deaktiverAlleRammekontroller()
        this.rammevalgSkjema.controls.rammer.controls.venstre.enable({emitEvent: false})
        this.rammevalgSkjema.controls.rammer.controls.topp.enable({emitEvent: false})
        this.rammevalgSkjema.controls.rammer.controls.hoeyre.enable({emitEvent: false})
        this.rammevalgSkjema.controls.rammer.controls.bunn.enable({emitEvent: false})
        return this.rammevalgSkjema;
    }

    private deaktiverAlleRammekontroller() {
        this.rammevalgSkjema.controls.rammer.controls.alle.disable({emitEvent: false})
        this.rammevalgSkjema.controls.rammer.controls.mellom.disable({emitEvent: false})
        this.rammevalgSkjema.controls.rammer.controls.omkring.disable({emitEvent: false})
        this.rammevalgSkjema.controls.rammer.controls.vertikalMellom.disable({emitEvent: false})
        this.rammevalgSkjema.controls.rammer.controls.horisontalMellom.disable({emitEvent: false})

        this.rammevalgSkjema.controls.rammer.controls.venstre.disable({emitEvent: false})
        this.rammevalgSkjema.controls.rammer.controls.topp.disable({emitEvent: false})
        this.rammevalgSkjema.controls.rammer.controls.hoeyre.disable({emitEvent: false})
        this.rammevalgSkjema.controls.rammer.controls.bunn.disable({emitEvent: false})
    }

    private oppdaterStilvalgFraValgteCeller(rammevalgKomp: RiktekstRedigeringTabellRammevalgKomponent) {
        let valgteCeller = valgteTabellCeller(this.editor().state.selection as CellSelection).map(celle => celle.node);

        //oppdater rammevalg
        const rammevalg = valgteCeller
          .map(node => node.attrs.ramme as Celleramme)
          .reduce((forrige, denne) =>
            erLikeRammevalg(forrige, denne) ? forrige : {} as Celleramme);

        if (rammevalg.bunn != undefined)
            this.rammevalgSkjema.patchValue(RiktekstRedigeringTabellRammevalgKomponent.fraCelleramme(rammevalg), {emitEvent: false});
        else
            this.rammevalgSkjema.reset(undefined, {emitEvent: false}); //emitEvent:false slik at ikke rammevalgSkjema.valueChanges trigges
        //siden vi oppdatere rammevalgSkjema med {emitEvent: false} må vi manuelt sørge for at endringer i GUI tegnet ut
        rammevalgKomp.oppdaterGUI();

        //oppdater bakgrunnsfarge
        const bakgrunnsfarge = valgteCeller
          .map(node => node.attrs.bakgrunnsfarge as string)
          .reduce((forrige, denne) =>
            forrige == denne ? forrige : '');
        if (erIkkeTomStreng(bakgrunnsfarge))
            this.bakgrunnsfargeKontroll.setValue(bakgrunnsfarge, {emitEvent: false});
        else
            this.bakgrunnsfargeKontroll.reset(undefined, {emitEvent: false}); //emitEvent:false slik at ikke bakgrunnsfargeKontroll.valueChanges trigges

        //oppdater marg
        const marg:number|null = valgteCeller
          .map(node => node.attrs.marg as number)
          .reduce((forrige, denne) =>
            forrige == denne ? forrige : null as any);
        this.margIPiksler.set(marg);
    }
}
